import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";
import { Plus, Search, FileDown, Pencil, Trash2 } from "lucide-react";

interface ForeignStaffItem {
  id: string;
  stt: number;
  function: string;
  name: string;
  phone: string;
  from: string;
  passportNumber: string;
  passportExpiry: string;
  visaExpiry: string;
  entryDate: string;
  attachments: string;
}

const mockData: ForeignStaffItem[] = [
  {
    id: "1",
    stt: 1,
    function: "- Chọn -",
    name: "sdsd >>",
    phone: "98",
    from: "Trung Quốc",
    passportNumber: "99999999999999999",
    passportExpiry: "29/09/2025",
    visaExpiry: "29/09/2025",
    entryDate: "29/08/2025",
    attachments: "",
  },
];

const ForeignStaff: React.FC = () => {
  const navigate = useNavigate();
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<ForeignStaffItem | null>(
    null
  );
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  return (
    <PageShell title="Cán bộ nước ngoài">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Cán bộ nước ngoài
          </h1>
          <button
            onClick={() => navigate("/add-new-foreign-staff")}
            className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>Thêm mới Cán bộ</span>
          </button>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white rounded-lg p-4">
          <div className="space-y-4">
            {/* Row 1 - Search input + Search button */}
            <div className="flex items-center space-x-4">
              <div className="relative w-96">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Tìm kiếm cán bộ nước ngoài"
                  className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button className="bg-green-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-green-700 transition-colors">
                Tìm kiếm
              </button>
            </div>

            {/* Row 2 - Filter dropdowns */}
            <div className="flex items-end space-x-2">
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Quốc gia -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Tổ chức -</option>
              </select>
              <select className="border border-gray-300 rounded-md px-2 py-2 text-sm bg-white w-36">
                <option>- Giới tính -</option>
              </select>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">
                  Hạn hộ chiếu
                </label>
                <input
                  id="passportExpiry"
                  type="date"
                  className="w-36 border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs text-gray-500 mb-1">
                  Hạn thị thực
                </label>
                <input
                  id="visaExpiry"
                  type="date"
                  className="w-36 border border-gray-300 rounded-md px-2 py-2 text-sm bg-white"
                />
              </div>
            </div>

            {/* Row 3 - Export button */}
            <div className="flex justify-start">
              <button className="bg-orange-500 text-white px-3 py-2 rounded text-sm font-medium hover:bg-orange-600 transition-colors flex items-center space-x-2">
                <FileDown className="h-4 w-4" />
                <span>Xuất ra file</span>
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-8">
                    STT
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Tên
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Điện thoại
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Đến từ
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-24">
                    Số hộ chiếu
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Hạn hộ chiếu
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Hạn thị thực
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Ngày nhập cảnh
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider border-r border-gray-200 w-20">
                    Đính kèm
                  </th>
                  <th className="px-2 py-3 text-left text-xs font-bold text-black uppercase tracking-wider w-20">
                    Chức năng
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {mockData.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-2 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 w-8">
                      {item.stt}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      <div className="font-medium text-[#337ab7] text-[14px] leading-tight">
                        {item.name}
                      </div>
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.phone}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.from}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-24">
                      {item.passportNumber}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.passportExpiry}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.visaExpiry}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.entryDate}
                    </td>
                    <td className="px-2 py-4 text-sm text-gray-900 border-r border-gray-200 w-20">
                      {item.attachments || (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-2 py-4 whitespace-nowrap w-20">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          title="Sửa"
                          aria-label="Sửa"
                          className="p-1 rounded text-[#337ab7] hover:bg-blue-50 hover:text-[#2e6da4] transition-colors"
                          onClick={() =>
                            navigate(`/edit-foreign-staff/${item.id}`)
                          }
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Xóa"
                          aria-label="Xóa"
                          className="p-1 rounded text-red-600 hover:bg-red-50 hover:text-red-700 transition-colors"
                          onClick={() => {
                            setItemToDelete(item);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination inside the table container */}
          <div className="flex justify-between items-center p-4 border-t border-gray-200">
            <div className="text-sm font-medium text-gray-700">
              Hiển thị <span className="font-bold">1</span> đến{" "}
              <select className="font-bold text-blue-600 border-none bg-transparent focus:ring-0 focus:outline-none text-sm">
                <option>10</option>
                <option>20</option>
                <option>50</option>
                <option>100</option>
              </select>{" "}
              trong <span className="font-bold">10000</span> kết quả
            </div>
            <div className="flex space-x-2">
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &lt;
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                1
              </button>
              <button className="px-3 py-2 text-sm font-medium text-white bg-[#337ab7] border border-[#337ab7] rounded-md">
                2
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                3
              </button>
              <button className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                &gt;
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center">
          <div className="bg-white rounded-md p-4 max-w-sm w-full mx-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Xác nhận xóa
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Bạn có chắc muốn xóa cán bộ{" "}
              <span className="font-semibold text-gray-900">
                {itemToDelete?.name}
              </span>
              ?
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setItemToDelete(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200"
              >
                Hủy
              </button>
              <button
                onClick={() => {
                  // TODO: delete item
                  setShowDeleteConfirm(false);
                  setItemToDelete(null);
                }}
                className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded hover:bg-red-700"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default ForeignStaff;
